import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as actionsApi from '@/api/actions.api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Ban, Eye, Send, ShieldCheck } from 'lucide-react';
import { cn, formatDateTime } from '@/utils/helpers';

/**
 * Shadow review (plan §9): what the manager would have sent while it runs in
 * shadow mode, and what the close gate would have reopened. Used to tune the
 * rules before a project goes live.
 */

type Tab = 'actions' | 'dod' | 'labels' | 'nudges';

const TYPE_LABELS: Record<string, string> = {
  'gitlab.issue.comment': 'Issue comment',
  'gitlab.issue.memory_note': 'Memory comment',
  'gitlab.issue.update': 'Issue change',
  'gitlab.mr.comment': 'MR comment',
  'gitlab.commit.comment': 'Commit comment',
  'gitlab.mr.merge': 'Merge',
  'gitlab.job.play': 'Run job',
  'rocketchat.dm': 'Chat DM',
  'rocketchat.post': 'Chat message',
};

function preview(action: actionsApi.OutboundAction): string {
  const p = action.payload;
  if (typeof p.body === 'string') return p.body;
  if (typeof p.text === 'string') return p.text;
  const parts: string[] = [];
  if (p.stateEvent) parts.push(`state: ${String(p.stateEvent)}`);
  if (Array.isArray(p.addLabels) && p.addLabels.length) parts.push(`add labels: ${p.addLabels.join(', ')}`);
  if (Array.isArray(p.removeLabels) && p.removeLabels.length) parts.push(`remove labels: ${p.removeLabels.join(', ')}`);
  if (Array.isArray(p.assigneeIds)) parts.push(`assign: ${p.assigneeIds.join(', ')}`);
  return parts.join(' · ') || JSON.stringify(p);
}

function ActionRow({ action }: { action: actionsApi.OutboundAction }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const approve = useMutation({
    mutationFn: () => actionsApi.approveAction(action._id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['actions'] }),
  });
  const text = preview(action);
  const question = typeof action.payload.question === 'string' ? action.payload.question : null;

  return (
    <div className="border-b py-3 last:border-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-foreground">{TYPE_LABELS[action.type] || action.type}</span>
            <span className="font-mono">{action.target.ref}</span>
            <span>{formatDateTime(action.createdAt)}</span>
            <span>by {action.requestedBy.id || action.requestedBy.role || action.requestedBy.kind}</span>
            <span className={cn(action.decision === 'shadowed' ? 'text-amber-700' : action.decision === 'executed' ? 'text-green-700' : 'text-red-700')}>
              {action.decision}
            </span>
          </div>
          <p className="mt-1 text-sm">{action.reason}</p>
          {question && <p className="mt-1 text-xs text-muted-foreground" dir="auto">Q: {question}</p>}
          <button onClick={() => setOpen(!open)} className="mt-1 text-xs text-primary hover:underline">
            {open ? 'Hide' : 'Show'} {action.type.startsWith('gitlab.issue.update') ? 'change' : 'text'}
          </button>
          {open && (
            <pre dir="auto" className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap rounded bg-muted p-3 text-xs">
              {text}
            </pre>
          )}
          {action.denyReason && <p className="mt-1 text-xs text-red-700">{action.denyReason}</p>}
          {action.lintNotes && action.lintNotes.length > 0 && <p className="mt-1 text-xs text-amber-700">{action.lintNotes.join('; ')}</p>}
        </div>
        {action.decision === 'shadowed' && (
          <div className="flex gap-2">
            {confirming ? (
              <>
                <Button size="sm" variant="destructive" disabled={approve.isPending} onClick={() => approve.mutate()}>
                  <Send className="mr-1 h-3 w-3" /> Send it now
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
                Send for real…
              </Button>
            )}
          </div>
        )}
      </div>
      {approve.isError && <p className="mt-1 text-xs text-red-700">{(approve.error as Error).message}</p>}
    </div>
  );
}

function ActionsTab() {
  const [type, setType] = useState('');
  const [decision, setDecision] = useState('shadowed');
  const { data: actions = [], isLoading } = useQuery({
    queryKey: ['actions', type, decision],
    queryFn: () => actionsApi.listActions({ type: type || undefined, decision: decision || undefined, limit: 200 }),
    refetchInterval: 60 * 1000,
  });
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const a of actions) c[a.type] = (c[a.type] || 0) + 1;
    return c;
  }, [actions]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">
            {actions.length} actions {Object.entries(counts).length > 0 && <span className="text-sm font-normal text-muted-foreground">({Object.entries(counts).map(([t, n]) => `${TYPE_LABELS[t] || t}: ${n}`).join(', ')})</span>}
          </CardTitle>
          <div className="flex gap-2">
            <select value={type} onChange={(e) => setType(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="">All types</option>
              {Object.entries(TYPE_LABELS).map(([t, label]) => (
                <option key={t} value={t}>
                  {label}
                </option>
              ))}
            </select>
            <select value={decision} onChange={(e) => setDecision(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="shadowed">Would have sent</option>
              <option value="executed">Sent</option>
              <option value="denied">Refused</option>
              <option value="deferred">Waiting for work hours</option>
              <option value="failed">Failed</option>
              <option value="">All</option>
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>{isLoading ? <LoadingSpinner /> : actions.length ? actions.map((a) => <ActionRow key={a._id} action={a} />) : <p className="text-sm text-muted-foreground">Nothing yet.</p>}</CardContent>
    </Card>
  );
}

function DodTab() {
  const { data, isLoading, error } = useQuery({ queryKey: ['dod-report'], queryFn: () => actionsApi.getDodReport() });
  const [showPassed, setShowPassed] = useState(false);
  if (isLoading) return <LoadingSpinner className="py-12" />;
  if (error || !data) return <p className="text-sm text-red-600">Could not load the report.</p>;
  const issues = data.issues.filter((i) => showPassed || !i.passed);
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        {Object.entries(data.byProfile).map(([profile, s]) => (
          <Card key={profile}>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Profile {profile}</p>
              <p className="text-2xl font-bold">
                {s.wouldReopen} / {s.checked}
              </p>
              <p className="text-xs text-muted-foreground">closed issues the gate would have reopened</p>
            </CardContent>
          </Card>
        ))}
        <Card>
          <CardContent className="p-4 text-sm">
            <p className="mb-1 text-muted-foreground">Most common reasons</p>
            {Object.entries(data.byCode)
              .sort((a, b) => b[1] - a[1])
              .map(([code, n]) => (
                <div key={code} className="flex justify-between">
                  <span className="font-mono text-xs">{code}</span>
                  <span>{n}</span>
                </div>
              ))}
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Closed issues</CardTitle>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={showPassed} onChange={(e) => setShowPassed(e.target.checked)} /> show the ones that pass
            </label>
          </div>
        </CardHeader>
        <CardContent>
          {issues.map((i) => (
            <div key={`${i.gitlabProjectId}-${i.iid}`} className="border-b py-2 text-sm last:border-0">
              <Link to={`/issues/${i.gitlabProjectId}/${i.iid}`} className="font-mono text-xs text-primary hover:underline">
                {i.ref}
              </Link>{' '}
              <span dir="auto">{i.title}</span>
              <span className="ml-2 text-xs text-muted-foreground">{i.profile}</span>
              {i.passed ? (
                <span className="ml-2 text-xs text-green-700">passes</span>
              ) : (
                <ul className="mt-1 list-disc pl-5 text-xs text-red-700">
                  {i.missing.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              )}
              {i.warnings.length > 0 && <p className="mt-0.5 pl-5 text-xs text-muted-foreground">{i.warnings.join(' · ')}</p>}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function NudgesTab() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const { data: plan, isLoading } = useQuery({ queryKey: ['ladder-preview', date], queryFn: () => actionsApi.getLadderPreview(date) });
  const { data: digest } = useQuery({ queryKey: ['digest-preview'], queryFn: actionsApi.getDigestPreview });
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base">Nudges the ladder would send</CardTitle>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
          </div>
          {plan && (
            <p className="text-xs text-muted-foreground">
              {plan.updates.length} issues move up · {plan.messages.length} DMs · {plan.comments.length} issue comments (none on weekends and holidays)
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading && <LoadingSpinner />}
          {plan?.messages.map((m) => (
            <div key={m.to}>
              <p className="text-sm font-medium">DM to @{m.to}</p>
              <pre dir="auto" className="whitespace-pre-wrap rounded bg-muted p-2 text-xs">
                {m.lines.map((l) => `- ${l}`).join('\n')}
              </pre>
            </div>
          ))}
          {plan?.comments.map((c) => (
            <div key={`${c.gitlabProjectId}-${c.iid}`}>
              <p className="text-sm font-medium">
                Comment on <Link to={`/issues/${c.gitlabProjectId}/${c.iid}`} className="text-primary hover:underline">#{c.iid}</Link>
              </p>
              <pre dir="auto" className="whitespace-pre-wrap rounded bg-muted p-2 text-xs">
                {c.body}
              </pre>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Today's digest (18:00, team channel)</CardTitle>
        </CardHeader>
        <CardContent>
          <pre dir="auto" className="whitespace-pre-wrap rounded bg-muted p-3 text-xs">
            {digest || '…'}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}

function LabelsTab() {
  const queryClient = useQueryClient();
  const { data: plan = [], isLoading } = useQuery({ queryKey: ['label-plan'], queryFn: actionsApi.getLabelPlan });
  const [confirming, setConfirming] = useState(false);
  const apply = useMutation({
    mutationFn: actionsApi.applyLabelPlan,
    onSuccess: () => {
      setConfirming(false);
      queryClient.invalidateQueries({ queryKey: ['actions'] });
    },
  });
  if (isLoading) return <LoadingSpinner className="py-12" />;
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{plan.length} open issues would get canonical labels</CardTitle>
          {plan.length > 0 &&
            (confirming ? (
              <div className="flex gap-2">
                <Button size="sm" disabled={apply.isPending} onClick={() => apply.mutate()}>
                  Request all {plan.length} changes
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
                Approve the relabel batch…
              </Button>
            ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Requests go through the gateway like everything else: in shadow mode they appear under “Would have sent”; on a live
          project they change GitLab.
        </p>
        {apply.data && (
          <p className="text-xs text-green-700">
            {apply.data.issues} requested: {Object.entries(apply.data.decisions).map(([d, n]) => `${n} ${d}`).join(', ')}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {plan.map((p) => (
          <div key={`${p.gitlabProjectId}-${p.iid}`} className="border-b py-2 text-sm last:border-0">
            <Link to={`/issues/${p.gitlabProjectId}/${p.iid}`} className="font-mono text-xs text-primary hover:underline">
              {p.ref}
            </Link>{' '}
            <span dir="auto">{p.title}</span>
            <div className="mt-1 flex flex-wrap gap-1 text-xs">
              {p.remove.map((l) => (
                <span key={`-${l}`} className="rounded bg-red-100 px-1.5 py-0.5 text-red-800 line-through">
                  {l}
                </span>
              ))}
              {p.add.map((l) => (
                <span key={`+${l}`} className="rounded bg-green-100 px-1.5 py-0.5 text-green-800">
                  + {l}
                </span>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function ShadowReview() {
  const [tab, setTab] = useState<Tab>('actions');
  const queryClient = useQueryClient();
  const { data: kill } = useQuery({ queryKey: ['kill-switch'], queryFn: actionsApi.getKillSwitch });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => actionsApi.setKillSwitch(enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kill-switch'] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Shadow review</h1>
          <p className="text-sm text-muted-foreground">
            What the manager would have posted or changed while running in shadow mode, and what the close gate would have
            reopened. Nothing here was sent unless it says “Sent”.
          </p>
        </div>
        <Button
          variant={kill?.global ? 'destructive' : 'outline'}
          size="sm"
          disabled={toggle.isPending || !kill}
          onClick={() => toggle.mutate(!kill?.global)}
        >
          {kill?.global ? <Ban className="mr-1 h-4 w-4" /> : <ShieldCheck className="mr-1 h-4 w-4" />}
          {kill?.global ? 'Kill switch ON: all outbound stopped (turn off)' : 'Stop all outbound actions'}
        </Button>
      </div>

      <div className="flex gap-2 border-b">
        {(
          [
            ['actions', 'Would have sent'],
            ['dod', 'Close gate dry run'],
            ['labels', 'Label clean-up'],
            ['nudges', 'Nudges and digest'],
          ] as Array<[Tab, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              '-mb-px flex items-center gap-1 border-b-2 px-3 py-2 text-sm',
              tab === key ? 'border-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Eye className="h-3 w-3" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'actions' && <ActionsTab />}
      {tab === 'dod' && <DodTab />}
      {tab === 'labels' && <LabelsTab />}
      {tab === 'nudges' && <NudgesTab />}
    </div>
  );
}
